import { Component, Prop, h, Element, State, Host, Watch, Event, EventEmitter, Listen } from '@stencil/core';
import { FormValue, RowValue, Root, PredefinedValues, Translationsi18n } from './types';
import { v4 as uuidv4 } from 'uuid';
import debounce from 'debounce';
import { plus } from 'pn-design-assets/pn-assets/icons.js';
import { translations } from './translations';

@Component({
  tag: 'pn-dropdown-with-multi-input-rows',
  styleUrl: 'pn-dropdown-with-multi-input-rows.scss',
})
export class PnDropdownWithMultiInputRows {
  @Element() hostElement: Element;
  pnFirstDropdown: HTMLElement = null;
  pnSecondDropdown: HTMLElement = null;
  pnSumField: HTMLElement = null;
  pnConfirmDeleteButton: HTMLElement = null;
  pnAbortDeleteButton: HTMLElement = null;

  @Prop() dropdownData: string | Root[] = null;
  @Prop() languageCode: string = null;
  @Prop() firstDropdownLabel: string = null;
  @Prop() firstDropdownHelperText: string = null;
  @Prop() secondDropdownLabel: string = null;
  @Prop() secondDropdownHelperText: string = null;
  @Prop() secondDropdownMax: number = null;
  @Prop() deleteMultipleRowsWarningText: string = null;
  @Prop() confirmDeleteButtonText: string = null;
  @Prop() abortDeleteButtonText: string = null;
  @Prop() firstInputLabel: string = null;
  @Prop() firstInputPlaceholder: string = null;
  @Prop() firstInputMin: number = null;
  @Prop() firstInputMax: number = null;
  @Prop() firstInputWarning: number = null;
  @Prop() firstInputWarningText: string = null;
  @Prop() firstInputLowErrorText: string = null;
  @Prop() firstInputHighErrorText: string = null;
  @Prop() secondInputLabel: string = null;
  @Prop() secondInputPlaceholder: string = null;
  @Prop() secondInputMin: number = null;
  @Prop() secondInputMax: number = null;
  @Prop() secondInputWarning: number = null;
  @Prop() secondInputWarningText: string = null;
  @Prop() secondInputLowErrorText: string = null;
  @Prop() secondInputHighErrorText: string = null;
  @Prop() disabledDates: string = null;
  @Prop() dateLabel: string = null;
  @Prop() datePlaceholder: string = null;
  @Prop() dateDaysFromToday: number = 1;
  @Prop() yearsToAddToEndDate: number = null;
  @Prop() sumFieldLabel: string = null;
  @Prop() addRowText: string = null;
  @Prop() elementHelperText: string = null;

  @State() dropdownDataRoot: Root[] = null;
  @State() secondDropdownArr: number[] = [];
  @State() rowData: PredefinedValues[] = null;
  @State() formValues: FormValue[] = null;
  @State() firstValidDateString: string = '';
  @State() lastValidDateString: string = '';
  @State() rowSum: string = null;
  @State() minRowCount: number = null;
  @State() showDeleteConfirm: boolean = false;
  @State() i18n: Translationsi18n;

  @Event() allFormValues: EventEmitter<string>;

  private _firstDropdownSelection: string = null;
  private _secondDropdownSelection: number = null;

  private _defaultRow: PredefinedValues;

  constructor() {
    this._defaultRow = {
      id: this.generateUniqueId(),
      firstInput: '',
      secondInput: this.secondInputWarning !== null ? this.secondInputWarning.toString() : '',
      date: '',
    };
  }

  componentWillLoad() {
    this.formValues = [];
    this.setDropDownData();
    this.updateSecondDropdown();
    this.setRowData();
    this.setFirstValidDate();
    this.setTranslations();
  }

  @Watch('languageCode')
  setTranslations() {
    this.i18n = translations[this.languageCode] || translations['en'];
  }

  componentDidLoad() {
    this.updateSum();
  }

  setDropDownData() {
    if (typeof this.dropdownData === 'string') {
      this.dropdownDataRoot = JSON.parse(this.dropdownData);
    } else {
      this.dropdownDataRoot = this.dropdownData;
    }

    if (this.dropdownDataRoot?.length) {
      this.dropdownDataRoot.forEach(element => {
        if (!element.value) {
          element.value = 0;
        }
      });
    }

    if (this.dropdownDataRoot.length > 0) {
      this.dropdownDataRoot[0].checked = true;
      this._firstDropdownSelection = this.dropdownDataRoot[0].label;
      this._secondDropdownSelection = this.dropdownDataRoot[0].value;
      this.minRowCount = this.dropdownDataRoot[0].value;
    }
  }

  updateSecondDropdown() {
    const selectedItem = this.dropdownDataRoot.find(arr => arr.checked === true);
    this.secondDropdownArr = [];

    for (let index = selectedItem.value; index <= this.secondDropdownMax; index++) {
      this.secondDropdownArr.push(index);
    }
  }

  updateSelectItemArr(arr: Root[], caption: string) {
    arr.forEach(item => {
      if (item.label === caption) {
        item.checked = !item.checked;
      } else {
        item.checked = false;
      }
    });
    return arr;
  }

  setRowData() {
    this.rowData = [];
    for (let index = 0; index < this._secondDropdownSelection; index++) {
      const row = Object.assign({}, this._defaultRow);
      row.id = this.generateUniqueId();
      this.rowData = [...this.rowData, row];
    }
  }

  generateUniqueId() {
    return uuidv4();
  }

  setFirstValidDate() {
    const yearsToadd = this.yearsToAddToEndDate ? this.yearsToAddToEndDate : 2;
    const date = new Date();
    date.setDate(date.getDate() + this.dateDaysFromToday);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const endYear = year + yearsToadd;

    this.firstValidDateString = `${year.toString()}-${month}-${day}`;
    this.lastValidDateString = `${endYear.toString()}-${month}-${day}`;
  }

  addRow(newRowId: string) {
    const newRow = Object.assign({}, this.rowData[0]);
    newRow.id = newRowId ? newRowId : this.generateUniqueId();
    newRow.date = '';
    this.rowData = [...this.rowData, newRow];

    this.secondDropdownArr = [...this.secondDropdownArr];
    this.updateSum();
    this.emitValuesIfValid();
  }

  handleFirstDropdown = debounce((e: Event) => {
    const selected = e.target as HTMLSelectElement | null;
    const selectedValue = selected?.value;

    if (!selectedValue?.length) {
      return;
    }

    const selectionRoot = this.dropdownDataRoot.find(x => x.value.toString() === selectedValue);
    this.minRowCount = selectionRoot.value;

    if (selectionRoot.value > this._secondDropdownSelection) {
      this.dropdownDataRoot = [...this.dropdownDataRoot];

      for (let index = this.rowData?.length; index < selectionRoot.value; index++) {
        this.addRow(this.generateUniqueId());
      }
    }

    this.updateSecondDropdown();
    this.updateSum();
    this.emitValuesIfValid();
  })

  handleSecondDropdown = debounce((e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    const selectedOption = target?.value;

    if (!selectedOption) {
      return;
    }

    const selectedValue = selectedOption

    if (!selectedValue?.length) {
      return;
    }

    const value = parseInt(selectedValue);

    if (value < this._secondDropdownSelection) {
      this.showDeleteConfirm = true;
    } else {
      for (let index = this.rowData?.length; index < value; index++) {
        this.addRow(this.generateUniqueId());
      }
    }

    this._secondDropdownSelection = value;

    this.updateSum();
    this.emitValuesIfValid();
  })

  @Listen('rowValueChange')
  @Listen('firstInputChange')
  @Listen('secondInputChange')
  predefinedValueChange(e) {
    const indexToUpdate = this.rowData?.findIndex(data => data.id === e.detail.id);
    const val: PredefinedValues = e.detail;
    this.rowData[indexToUpdate] = {
      ...this.rowData[indexToUpdate],
      firstInput: val.firstInput,
      secondInput: val.secondInput,
      date: val.date,
    };

    if (e.type === 'firstInputChange' && indexToUpdate === 0) {
      this.updateFirstInputOfRows(val.firstInput);
    }

    if (e.type === 'secondInputChange' && indexToUpdate === 0) {
      this.updateSecondInputOfRows(val.secondInput);
    }

    this.rowData = [...this.rowData];
    this.emitValuesIfValid();
    this.updateSum();
  }

  updateFirstInputOfRows(value: string) {
    for (let index = 1; index < this.rowData?.length; index++) {
      this.rowData[index] = {
        ...this.rowData[index],
        firstInput: value,
      };
    }
  }

  updateSecondInputOfRows(value: string) {
    for (let index = 1; index < this.rowData?.length; index++) {
      this.rowData[index] = {
        ...this.rowData[index],
        secondInput: value,
      };
    }
  }

  @Listen('deleteRowEvent')
  deleteRowEvent(e) {
    const indexToRemove = this.rowData?.findIndex(data => data.id === e.detail);
    if (indexToRemove < 0) {
      return;
    }

    this.rowData?.splice(indexToRemove, 1);
    this.rowData = [...this.rowData];

    this.secondDropdownArr = [...this.secondDropdownArr];
    this.emitValuesIfValid();
    this.updateSum();
  }

  updateSum() {
    this.rowSum = '';
    let sum = 0;
    this.rowData?.forEach(element => {
      if (element?.firstInput?.length) {
        sum += parseInt(element.firstInput);
      }
    });

    this.rowSum = sum.toString();
  }

  deleteExcessRows() {
    if (this._secondDropdownSelection === null) {
      return;
    }

    const rowsToDelete = this.rowData?.length - this._secondDropdownSelection;
    for (let index = 0; index < rowsToDelete; index++) {
      this.rowData?.pop();
    }
    this.rowData = [...this.rowData];
    this.showDeleteConfirm = false;
    this.updateSum();
  }

  abortDeleteRows() {
    this.showDeleteConfirm = false;
  }

  emitValuesIfValid() {
    this.formValues = [];
    this.rowData?.forEach(item => {
      const rowValue: FormValue = { row: [] };
      if (this._firstDropdownSelection?.length) {
        const dropDownValue: RowValue = { name: this.firstDropdownLabel, value: this._firstDropdownSelection };
        rowValue.row.push(dropDownValue);
      }

      if (item.firstInput?.length) {
        const firstInputValue: RowValue = { name: this.firstInputLabel, value: item.firstInput };
        rowValue.row.push(firstInputValue);
      }

      if (item.secondInput?.length) {
        const secondInputValue: RowValue = { name: this.secondInputLabel, value: item.secondInput };
        rowValue.row.push(secondInputValue);
      }

      if (item.date) {
        const dateValue: RowValue = { name: this.dateLabel, value: item.date };
        rowValue.row.push(dateValue);
      }

      if (rowValue.row.length === 4) {
        this.formValues.push(rowValue);
      }
    });

    if (this.formValues.length === this.rowData?.length) {
      const valueJsonString = JSON.stringify(this.formValues);
      this.allFormValues.emit(valueJsonString);
    } else {
      this.allFormValues.emit('');
    }
  }

  render() {
    return (
      <Host class="dropdown-with-multi-input-element">
        <div class="dropdown-with-multi-input-element__row">
          <div class="dropdown-with-multi-input-element__dropdown">
            <pn-select ref={el => (this.pnFirstDropdown = el as HTMLElement)} onChange={debounce(this.handleFirstDropdown.bind(this), 500)} class="pn-row-select-dropdown dropDown1-dropdown" label={this.firstDropdownLabel} language={this.languageCode}>
              <option value="" disabled selected>{this.i18n.dropdownPlaceholder}</option>
              {this.dropdownDataRoot?.map((item) => (
                <option value={item.value}>{item.label}</option>
              ))}
            </pn-select>
            {this.firstDropdownHelperText?.length ? <small>{this.firstDropdownHelperText}</small> : null}
          </div>
        </div>

        <div class="dropdown-with-multi-input-element__row">
          <div class="dropdown-with-multi-input-element__dropdown">
            <pn-select ref={el => (this.pnSecondDropdown = el as HTMLElement)} onChange={debounce(this.handleSecondDropdown.bind(this), 500)} class="pn-row-select-dropdown dropDown2-dropdown" label={this.secondDropdownLabel} language={this.languageCode}>
              <option value="" disabled selected>{this.i18n.dropdownPlaceholder}</option>
              {this.secondDropdownArr?.map((item) => (
                <option value={item}>{item}</option>
              ))}
            </pn-select>
            {this.secondDropdownHelperText?.length ? <small>{this.secondDropdownHelperText}</small> : null}
          </div>
        </div>

        {this.showDeleteConfirm ? (
          <div class="dropdown-with-multi-input-element__delete">
            <p>{this.deleteMultipleRowsWarningText}</p>
            <pn-button ref={el => (this.pnConfirmDeleteButton = el as HTMLElement)} small="true" appearance="warning" onClick={this.deleteExcessRows.bind(this)}>
              {this.confirmDeleteButtonText}
            </pn-button>
            <pn-button ref={el => (this.pnAbortDeleteButton = el as HTMLElement)} small="true" appearance="light" onClick={this.abortDeleteRows.bind(this)}>
              {this.abortDeleteButtonText}
            </pn-button>
          </div>
        ) : null}

        <div class="dropdown-with-multi-input-element__label-row">
          <div class="dropdown-with-multi-input-element__label">
            <label>{this.firstInputLabel}</label>
          </div>
          <div class="dropdown-with-multi-input-element__label">
            <label>{this.secondInputLabel}</label>
          </div>
          <div class="dropdown-with-multi-input-element__label">
            <label>{this.dateLabel}</label>
          </div>
        </div>

        {this.rowData?.map((data, i) => {
          return (
            <div class={'dropdown-with-multi-input-element__row generated-row-' + i} id={'generated-row-' + i}>
              <pn-dropdown-with-multi-input-rows-row
                language-code={this.languageCode}
                first-input-placeholder={this.firstInputPlaceholder}
                first-input-min={this.firstInputMin}
                first-input-max={this.firstInputMax}
                first-input-warning={this.firstInputWarning}
                first-input-warning-text={this.firstInputWarningText}
                first-input-low-error-text={this.firstInputLowErrorText}
                first-input-high-error-text={this.firstInputHighErrorText}
                second-input-placeholder={this.secondInputPlaceholder}
                second-input-min={this.secondInputMin}
                second-input-max={this.secondInputMax}
                second-input-warning={this.secondInputWarning}
                second-input-warning-text={this.secondInputWarningText}
                second-input-low-error-text={this.secondInputLowErrorText}
                second-input-high-error-text={this.secondInputHighErrorText}
                datePlaceholder={this.datePlaceholder}
                firstValidDate={this.firstValidDateString}
                lastValidDate={this.lastValidDateString}
                predefined-value={JSON.stringify(data)}
                min-row-count={this.minRowCount}
                row-index={i}
                disabled-dates={this.disabledDates}
              ></pn-dropdown-with-multi-input-rows-row>
            </div>
          );
        })}

        <div class="dropdown-with-multi-input-element__row sum-row">
          <pn-input disabled={true} ref={el => (this.pnSumField = el as HTMLElement)} type="number" value={this.rowSum} label={this.sumFieldLabel}></pn-input>

          <pn-button
            tooltip={this.addRowText ? this.addRowText : null}
            left-icon="true"
            arialabel="Add"
            icon={plus}
            appearance="light"
            variant="borderless"
            class="connected-dropdown__button-right"
            onClick={() => {
              this.addRow(this.generateUniqueId());
            }}
          >
            {this.addRowText}
          </pn-button>
        </div>

        {this.elementHelperText?.length ? <div class="dropdown-with-multi-input-element__row">{this.elementHelperText}</div> : null}
      </Host>
    );
  }
}
